/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / ORDERS-SCHWUND — „Schwund heute"-Block (read-only)
   =============================================================================
   S201 REF-7e: aus orders.js extrahiert (Null Verhaltensänderung).
   Schwund = order_corrections OHNE TSE (tse_transaction_id IS NULL) — interner
   Warenverlust, KEIN Umsatz, KEIN Kassenbeleg. Daten async über
   `orders.listSchwund({date})`; Lazy-Load in einen Modul-Cache + genau EIN
   nicht-destruktives Re-Render (inflight-aware, Memory gg-microtask-render-loop).
   Zählt NICHT in die Topbar-/Spalten-Counter.

   Der Re-Render nach dem Load läuft NICHT über einen direkten renderOrders-Import
   (das wäre ein Zyklus Orchestrator↔Schwund), sondern über den `onLoaded`-Callback,
   den der Orchestrator beim Aufruf mitgibt (= renderOrders). Verhalten identisch.
============================================================================ */

import { htmlEscape, fmtEUR, shortTime } from '../../utils/helpers.js';
import { schwundCategoryLabel } from './orders-split.js';
import { todayISO } from './orders-data.js';

let _schwundToday = [];
let _schwundLoadedForDate = null;
let _schwundInflight = false;

/** Aktueller Schwund-Cache (für den Orchestrator-Render). */
export function getSchwundToday() {
  return _schwundToday;
}

/** Same-Day-Invalidierung: nach einem Schwund-Write (orders-split) neu laden. */
export function invalidateSchwundToday() {
  _schwundLoadedForDate = null;
}

/**
 * Lazy-Load der heutigen Schwund-Positionen (fire-and-forget, ein Re-Render).
 * @param {Function} [onLoaded] wird nach dem Load aufgerufen (= renderOrders).
 */
export function ensureSchwundTodayLoaded(onLoaded) {
  const today = todayISO();
  if (_schwundLoadedForDate === today) return; // Cache frisch → kein Refetch
  if (_schwundInflight) return;                // inflight-aware → kein Render-Sturm
  const storage = window.App && window.App.storage && window.App.storage.orders;
  if (!storage || typeof storage.listSchwund !== 'function') return;
  _schwundInflight = true;
  Promise.resolve(storage.listSchwund({ date: today }))
    .then(rows => { _schwundToday = Array.isArray(rows) ? rows : []; })
    .catch(err => { console.warn('[orders] listSchwund:', err); })
    .finally(() => {
      _schwundInflight = false;
      _schwundLoadedForDate = today; // Versuch markiert (Erfolg ODER Fehler) → kein Retry-Sturm
      try { if (typeof onLoaded === 'function') onLoaded(); } catch (_) {}
    });
}

/** Pure: Anzahl + Summe (cents) der heutigen Schwund-Positionen. */
export function _aggregateSchwundToday(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return {
    count: list.length,
    amountCents: list.reduce((s, r) => s + (Number(r.refund_amount_cents) || 0), 0)
  };
}

function renderSchwundCard(r) {
  const label  = schwundCategoryLabel(r.reason_category);
  const amount = fmtEUR((Number(r.refund_amount_cents) || 0) / 100);
  const qty    = Number(r.qty_cancelled) || 0;
  const time   = r.cancelled_at ? shortTime(r.cancelled_at) : '';
  const reason = r.reason ? htmlEscape(String(r.reason)) : '';
  return `
    <div class="orders-schwund-card">
      <div class="orders-schwund-card-head">
        <span class="orders-schwund-cat badge-soft badge-soft-warn">${htmlEscape(label)}</span>
        <span class="orders-schwund-amount">${amount}</span>
      </div>
      <div class="orders-schwund-card-meta">
        ${qty ? `<span>${qty}×</span>` : ''}
        ${time ? `<span>${htmlEscape(time)}</span>` : ''}
      </div>
      ${reason ? `<div class="orders-schwund-reason">${reason}</div>` : ''}
    </div>
  `;
}

/** Pure-Render-Helper (exportiert für Tests). Leer → ganzer Block entfällt. */
export function renderSchwundTodaySection(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return '';
  const { count, amountCents } = _aggregateSchwundToday(list);
  return `
    <details class="orders-schwund">
      <summary>🗑️ Schwund heute (${count}) · ${fmtEUR(amountCents / 100)}</summary>
      <div class="orders-schwund-hint">Interner Warenverlust — kein Umsatz, kein Kassenbeleg, keine TSE.</div>
      <div class="orders-schwund-body">
        ${list.map(renderSchwundCard).join('')}
      </div>
    </details>
  `;
}

// invalidateSchwundToday wird von orders-split.js via window aufgerufen.
if (typeof window !== 'undefined') {
  Object.assign(window, { invalidateSchwundToday });
}
